import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Mail, Lock, ArrowRight, User } from 'lucide-react';
import { formatError } from '../utils/errorUtils';

export default function CustomerLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginCustomer } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await loginCustomer(email, password);
      toast.success('Welcome back!');
      navigate('/search');
    } catch (error) {
      const message = formatError(error.response?.data?.detail) || 'Invalid credentials';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-50 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-12 justify-center" data-testid="logo-link">
          <Building2 className="w-8 h-8 text-sky-500" />
          <span className="text-xl font-heading font-bold text-navy-900">EstateCloud</span>
        </Link>

        <div className="bg-white rounded-xl shadow-soft p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-sky-500" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-navy-900 mb-2">Customer Login</h1>
            <p className="text-navy-600">Sign in to access your favorites</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="customer-login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-navy-900 hover:bg-navy-800"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </form>

          <p className="mt-6 text-center text-navy-600">
            Don't have an account?{' '}
            <Link to="/customer/register" className="text-sky-500 font-medium hover:text-sky-600" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-navy-500 text-sm">
          Are you an agency?{' '}
          <Link to="/login" className="text-sky-500 font-medium">Agency Login</Link>
        </p>
      </div>
    </div>
  );
}
